// src/pages/Businesses.tsx

import React, { useState, useEffect } from 'react';
import { Building2, Plus, Calendar, Shield, Edit2, X, Network, PhoneOutgoing, ShieldOff, FileText } from 'lucide-react';
import { organizationsService, Organization } from 'api/services/organizations.service';
import { useAuth } from 'hooks/useAuth';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import BusinessOnboardingModal from 'components/Modals/BusinessOnboardingModal';
import ServiceProviderOnboardingModal from 'components/Modals/ServiceProviderOnboardingModal';
import BillingProfileModal from 'pages/Billing/BillingProfileModal';
import { canEditBillingProfile } from 'pages/Billing/billingProfileRules';

const Businesses: React.FC = () => {
  const { currentOrganization } = useAuth();
  const [businesses, setBusinesses] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [showServiceProviderModal, setShowServiceProviderModal] = useState(false);

  // Master-admin org edit (incl. re-parenting)
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; description: string; active: boolean; parentOrganizationId: string }>({
    name: '', description: '', active: true, parentOrganizationId: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // Billing profile (who the organisation is on an invoice), opened per card.
  const [profileOrg, setProfileOrg] = useState<Organization | null>(null);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;

      if (currentOrganization?.role === 'master_admin') {
        // Master admin sees all service providers AND all businesses combined
        const [serviceProvidersRes, businessesRes] = await Promise.all([
          organizationsService.getServiceProviders(),
          organizationsService.getAllBusinesses()
        ]);

        if (serviceProvidersRes.success && businessesRes.success) {
          // Combine service providers and businesses
          response = {
            success: true,
            data: [...(serviceProvidersRes.data || []), ...(businessesRes.data || [])]
          };
        } else {
          setError('Failed to load organizations');
          return;
        }
      } else if (currentOrganization?.role === 'super_admin') {
        // Super admin sees all businesses
        response = await organizationsService.getAllBusinesses();
      } else if (currentOrganization?.role === 'service_admin' && currentOrganization?.id) {
        // Service admin sees businesses under their service
        response = await organizationsService.getBusinessesByServiceProvider(currentOrganization.id);
      } else {
        setError('You do not have permission to view businesses');
        return;
      }

      if (response.success && response.data) {
        setBusinesses(response.data);
      } else {
        setError(response.message || 'Failed to load businesses');
      }
    } catch (err: any) {
      console.error('Error loading businesses:', err);
      setError(err.message || 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinesses();
  }, [currentOrganization]);

  const handleAddServiceProvider = () => {
    setShowServiceProviderModal(true);
  };

  const handleAddBusiness = () => {
    setShowBusinessModal(true);
  };

  const handleSuccess = () => {
    loadBusinesses();
  };

  const orgNameById = (id?: string) =>
    id ? (businesses.find(b => b.id === id)?.name || id) : '';
  // Outbound calling is a per-organisation capability a master admin grants. It
  // is shown here rather than on a settings page because this is where someone
  // already comes to reason about a tenant, and because a permission to spend on
  // carrier minutes should sit beside the tenant it spends for.
  const [capBusy, setCapBusy] = useState<string | null>(null);
  const [capError, setCapError] = useState<string | null>(null);

  const setOutbound = async (org: any, enable: boolean) => {
    setCapError(null);
    // A reason is required to ENABLE and optional to revoke — the server enforces
    // this too; asking here keeps the round trip from being wasted.
    let reason = '';
    if (enable) {
      reason = window.prompt(
        `Enable outbound calling for ${org.name}?\n\n` +
        'This lets its seated agents place calls on its own trunk, billed to it.\n' +
        'Reason (recorded in the audit log):'
      ) || '';
      if (!reason.trim()) return;
    }
    setCapBusy(org.id);
    try {
      const qs = `?enabled=${enable}` + (reason ? `&reason=${encodeURIComponent(reason)}` : '');
      await axiosInstance.post(API_ENDPOINTS.OUTBOUND_CAPABILITY(org.id) + qs);
      loadBusinesses();
    } catch (e: any) {
      setCapError(e?.response?.data?.message || e?.message || 'Could not change outbound calling');
    } finally {
      setCapBusy(null);
    }
  };


  const openEdit = (org: Organization) => {
    setEditingOrg(org);
    setEditForm({
      name: org.name || '',
      description: org.description || '',
      active: org.active,
      parentOrganizationId: org.parentOrganizationId || ''
    });
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingOrg) return;
    setEditError(null);
    setSavingEdit(true);
    const res = await organizationsService.updateOrganization(editingOrg.id, {
      name: editForm.name.trim() || undefined,
      description: editForm.description,
      active: editForm.active,
      // "" => move to root; backend treats undefined as unchanged, so always send the field
      // Only a master admin may reparent, and the server refuses the field
      // outright for anyone else — so sending it unchanged would turn "rename this
      // business" into "you may not move organisations" for every provider admin.
      ...(isMasterAdmin ? { parentOrganizationId: editForm.parentOrganizationId } : {})
    });
    setSavingEdit(false);
    if (res.success) {
      setEditingOrg(null);
      loadBusinesses();
    } else {
      setEditError(res.error || 'Failed to update organization');
    }
  };

  // Check if user has permission
  const isMasterAdmin = currentOrganization?.role === 'master_admin';
  // A service provider administers its own businesses — it can already CREATE
  // them (POST /organizations/business permits these roles), so hiding edit left
  // it able to make a child and never correct it. The server still decides what
  // each caller may reach: hasOrganizationAccess limits edits to organisations in
  // their own tree, and reparenting stays master-admin only.
  const canEditOrgs = ['master_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin']
    .includes(currentOrganization?.role || '');
  const canAddBusiness = currentOrganization?.role === 'master_admin' ||
                         currentOrganization?.role === 'super_admin' ||
                         currentOrganization?.role === 'service_admin';

  if (loading) return <LoadingSpinner />;
  if (error && !businesses.length) return <ErrorMessage message={error} onRetry={loadBusinesses} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            {isMasterAdmin ? 'Organizations' : 'Businesses'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total: {businesses.length} {isMasterAdmin ? 'organizations (service providers & businesses)' : 'businesses'}
            {!canAddBusiness && <span className="ml-2 text-xs text-orange-600">(Read-only)</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {isMasterAdmin && (
            <button
              onClick={handleAddServiceProvider}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Shield className="w-5 h-5" />
              Onboard Service Provider
            </button>
          )}
          {canAddBusiness && (
            <button
              onClick={handleAddBusiness}
              className="tl-btn tl-btn-primary"
            >
              <Plus className="w-5 h-5" />
              Onboard Business
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {businesses.map((business) => {
          const isServiceProvider = business.type === 'SERVICE';
          return (
            <div
              key={business.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-md transition ${
                isServiceProvider ? 'border-l-4 border-purple-500' : 'border-l-4 border-blue-500'
              }`}
            >
              {/* Two rows, not one.
                  Everything here used to share a single horizontal line: icon,
                  name, Service Provider badge, status and the edit button. On a
                  ~390px card that is five things competing for one row, so a long
                  name squeezed the rest and the layout read as cramped even once
                  it stopped overflowing. Splitting it gives the name the full
                  width and leaves the controls a fixed, predictable place. */}

              {/* Row 1 — icon, identity, controls.
                  The business id and Service Provider badge ride ALONGSIDE the
                  icon rather than below the name: they are short, the icon tile is
                  40px tall, and stacking them cost a whole line of blank space on
                  every card. The name still gets its own full-width line below,
                  which is what stopped it squeezing the controls. */}
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`shrink-0 p-2 rounded-lg ${
                    isServiceProvider ? 'bg-purple-100 dark:bg-purple-900/50' : 'bg-blue-100 dark:bg-blue-900/50'
                  }`}>
                    {isServiceProvider ? (
                      <Shield className="w-6 h-6 text-purple-600" />
                    ) : (
                      <Building2 className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                    <span className="text-xs text-gray-500 dark:text-gray-400 break-all">{business.businessId}</span>
                    {isServiceProvider && (
                      <span className="whitespace-nowrap px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded">
                        Service Provider
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span
                    className={`whitespace-nowrap px-2 py-1 text-xs rounded-full ${
                      business.active
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {business.active ? 'Active' : 'Inactive'}
                  </span>
                  {isMasterAdmin && (
                    <button
                      onClick={() => setOutbound(business, !business.outboundCallingEnabled)}
                      disabled={capBusy === business.id}
                      className={`p-1.5 rounded-lg transition disabled:opacity-40 ${
                        business.outboundCallingEnabled
                          ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                          : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      title={business.outboundCallingEnabled
                        ? 'Outbound calling is ON — click to revoke'
                        : 'Outbound calling is OFF — click to grant'}
                    >
                      <PhoneOutgoing className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setProfileOrg(business)}
                    className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                    title="Billing profile"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  {canEditOrgs && (
                    <button
                      onClick={() => openEdit(business)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                      title="Edit organization"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2 — the name, full width to wrap into. */}
              <h3 className="font-semibold text-gray-900 dark:text-white break-words leading-snug mb-3">
                {business.name}
              </h3>

              {business.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{business.description}</p>
              )}


              {/* A business that has withdrawn provider access is shown as such here,

                  read-only. The provider cannot change it — that would be consent restored

                  by the party it was withdrawn from — but seeing it explains why their

                  screens are empty for that business, which otherwise looks like a fault. */}

              {(business as any).allowProviderAccess === false && (

                <div className="flex items-start gap-2 text-xs rounded-lg px-2.5 py-2 mb-3 bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">

                  <ShieldOff className="w-4 h-4 shrink-0 mt-0.5" />

                  <span>This business has withdrawn provider access. Only they can restore it.</span>

                </div>

              )}

              {!isServiceProvider && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                  <Network className="w-4 h-4" />
                  <span>Parent: <span className="font-medium text-gray-700 dark:text-gray-300">{orgNameById(business.parentOrganizationId) || '— (root)'}</span></span>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>
                  Created: {new Date(business.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {businesses.length === 0 && !loading && (
        <div className="text-center py-12">
          {isMasterAdmin ? (
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          ) : (
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          )}
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No {isMasterAdmin ? 'organizations' : 'businesses'} yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Get started by onboarding your first {isMasterAdmin ? 'service provider or business' : 'business'}
          </p>
          <div className="flex gap-3 justify-center">
            {isMasterAdmin && (
              <button
                onClick={handleAddServiceProvider}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 inline-flex items-center gap-2"
              >
                <Shield className="w-5 h-5" />
                Onboard Service Provider
              </button>
            )}
            {canAddBusiness && (
              <button
                onClick={handleAddBusiness}
                className="tl-btn tl-btn-primary"
              >
                <Plus className="w-5 h-5" />
                Onboard Business
              </button>
            )}
          </div>
        </div>
      )}

      {showBusinessModal && (
        <BusinessOnboardingModal
          onClose={() => setShowBusinessModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showServiceProviderModal && (
        <ServiceProviderOnboardingModal
          onClose={() => setShowServiceProviderModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {profileOrg && (
        <BillingProfileModal
          organizationId={profileOrg.id}
          organizationName={profileOrg.name}
          editable={canEditBillingProfile(currentOrganization?.role, profileOrg.id, currentOrganization?.id)}
          onClose={() => setProfileOrg(null)}
        />
      )}

      {editingOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Organization
              </h2>
              <button onClick={() => setEditingOrg(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{editError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" value={editForm.name}
                       onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                <p className="text-xs text-gray-400 mt-1">Business ID: {editingOrg.businessId || '—'} · {editingOrg.type}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Parent organization
                </label>
                <select
                  value={editForm.parentOrganizationId}
                  onChange={(e) => setEditForm({ ...editForm, parentOrganizationId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">— (root / no parent) —</option>
                  {businesses
                    .filter(o => o.id !== editingOrg.id)
                    .map(o => (
                      <option key={o.id} value={o.id}>
                        {o.name}{o.type === 'SERVICE' ? ' (Service Provider)' : ''}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Moves this organization (and its sub-tree) under the selected parent. Cycles are rejected.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input type="text" value={editForm.description}
                       onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editForm.active}
                       onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                       className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setEditingOrg(null)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={savingEdit}
                      className="tl-btn tl-btn-primary disabled:opacity-50">
                {savingEdit && <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Businesses;
